import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useCallback } from 'react';
import {
    notificationsApi,
    GetNotificationsParams,
    CreateNotificationDto,
} from '@/services/api/notificationsApi';
import { Notification } from '@/types/notification';
import { toast } from 'sonner';
import { extractErrorMessage } from '@/utils/errorHandling';
import { aiGatewayService } from '@/services/aiGateway';

// Query keys factory
export const notificationKeys = {
    all: ['notifications'] as const,
    lists: () => [...notificationKeys.all, 'list'] as const,
    list: (params?: GetNotificationsParams) => [...notificationKeys.lists(), params] as const,
    details: () => [...notificationKeys.all, 'detail'] as const,
    detail: (id: string) => [...notificationKeys.details(), id] as const,
    unreadCount: () => [...notificationKeys.all, 'unread-count'] as const,
    statistics: () => [...notificationKeys.all, 'statistics'] as const,
};

// ==================== Query Hooks ====================

/**
 * Hook to fetch notifications with filters and pagination
 */
export const useNotifications = (params?: GetNotificationsParams) => {
    return useQuery({
        queryKey: notificationKeys.list(params),
        queryFn: () => notificationsApi.getNotifications(params),
        staleTime: 10000, // 10 seconds - notifications should be relatively fresh
    });
};

/**
 * Hook to fetch a single notification by ID
 */
export const useNotification = (id: string) => {
    return useQuery({
        queryKey: notificationKeys.detail(id),
        queryFn: () => notificationsApi.getNotification(id),
        enabled: !!id,
        staleTime: 30000, // 30 seconds
    });
};

/**
 * Hook to fetch unread notifications count
 */
export const useUnreadCount = () => {
    return useQuery({
        queryKey: notificationKeys.unreadCount(),
        queryFn: () => notificationsApi.getUnreadCount(),
        staleTime: 5000, // 5 seconds - frequently updated
        refetchInterval: 30000, // Refetch every 30 seconds
    });
};

/**
 * Hook to fetch notification statistics
 */
export const useNotificationStatistics = () => {
    return useQuery({
        queryKey: notificationKeys.statistics(),
        queryFn: () => notificationsApi.getStatistics(),
        staleTime: 60000, // 1 minute
    });
};

// ==================== Mutation Hooks ====================

/**
 * Hook to mark a notification as read
 */
export const useMarkAsRead = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id: string) => notificationsApi.markAsRead(id),
        onSuccess: (data, id) => {
            // Update the notification in cache
            queryClient.setQueryData(notificationKeys.detail(id), data);

            // Invalidate lists and counts
            queryClient.invalidateQueries({ queryKey: notificationKeys.lists() });
            queryClient.invalidateQueries({ queryKey: notificationKeys.unreadCount() });
            queryClient.invalidateQueries({ queryKey: notificationKeys.statistics() });
        },
        onError: (error) => {
            toast.error(extractErrorMessage(error) || 'Failed to mark notification as read');
        },
    });
};

/**
 * Hook to mark all notifications as read
 */
export const useMarkAllAsRead = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: () => notificationsApi.markAllAsRead(),
        onSuccess: () => {
            // Invalidate all notification queries
            queryClient.invalidateQueries({ queryKey: notificationKeys.all });
            toast.success('All notifications marked as read');
        },
        onError: (error) => {
            toast.error(extractErrorMessage(error) || 'Failed to mark all notifications as read');
        },
    });
};

/**
 * Hook to archive a notification
 */
export const useArchiveNotification = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id: string) => notificationsApi.archiveNotification(id),
        onSuccess: (data, id) => {
            // Update the notification in cache
            queryClient.setQueryData(notificationKeys.detail(id), data);

            // Invalidate lists and statistics
            queryClient.invalidateQueries({ queryKey: notificationKeys.lists() });
            queryClient.invalidateQueries({ queryKey: notificationKeys.statistics() });
            toast.success('Notification archived');
        },
        onError: (error) => {
            toast.error(extractErrorMessage(error) || 'Failed to archive notification');
        },
    });
};

/**
 * Hook to unarchive a notification
 */
export const useUnarchiveNotification = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id: string) => notificationsApi.unarchiveNotification(id),
        onSuccess: (data, id) => {
            // Update the notification in cache
            queryClient.setQueryData(notificationKeys.detail(id), data);

            // Invalidate lists and statistics
            queryClient.invalidateQueries({ queryKey: notificationKeys.lists() });
            queryClient.invalidateQueries({ queryKey: notificationKeys.statistics() });
            toast.success('Notification unarchived');
        },
        onError: (error) => {
            toast.error(extractErrorMessage(error) || 'Failed to unarchive notification');
        },
    });
};

/**
 * Hook to acknowledge a critical notification
 */
export const useAcknowledgeNotification = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id: string) => notificationsApi.acknowledgeNotification(id),
        onSuccess: (data, id) => {
            // Update the notification in cache
            queryClient.setQueryData(notificationKeys.detail(id), data);

            // Invalidate lists
            queryClient.invalidateQueries({ queryKey: notificationKeys.lists() });
            toast.success('Notification acknowledged');
        },
        onError: (error) => {
            toast.error(extractErrorMessage(error) || 'Failed to acknowledge notification');
        },
    });
};

/**
 * Hook to delete a notification
 */
export const useDeleteNotification = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id: string) => notificationsApi.deleteNotification(id),
        onSuccess: () => {
            // Invalidate all notification queries
            queryClient.invalidateQueries({ queryKey: notificationKeys.all });
            toast.success('Notification deleted');
        },
        onError: (error) => {
            toast.error(extractErrorMessage(error) || 'Failed to delete notification');
        },
    });
};

/**
 * Hook to bulk delete notifications
 */
export const useBulkDeleteNotifications = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (ids: string[]) => notificationsApi.bulkDeleteNotifications(ids),
        onSuccess: (_, ids) => {
            // Invalidate all notification queries
            queryClient.invalidateQueries({ queryKey: notificationKeys.all });
            toast.success(`${ids.length} notification(s) deleted`);
        },
        onError: (error) => {
            toast.error(extractErrorMessage(error) || 'Failed to delete notifications');
        },
    });
};

/**
 * Hook to create a notification (internal/system use)
 */
export const useCreateNotification = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (data: CreateNotificationDto) => notificationsApi.createNotification(data),
        onSuccess: () => {
            // Invalidate all notification queries
            queryClient.invalidateQueries({ queryKey: notificationKeys.all });
            toast.success('Notification created');
        },
        onError: (error) => {
            toast.error(extractErrorMessage(error) || 'Failed to create notification');
        },
    });
};

// ==================== Real-time WebSocket Hook ====================

export interface UseNotificationStreamOptions {
    /**
     * Enable/disable the stream
     * @default true
     */
    enabled?: boolean;

    /**
     * Callback when a new notification arrives
     */
    onNotification?: (notification: Notification) => void;

    /**
     * Show toast notification when new notification arrives
     * @default true
     */
    showToast?: boolean;

    /**
     * Auto-invalidate queries when notification arrives
     * @default true
     */
    autoInvalidate?: boolean;
}

/**
 * Hook to receive real-time notifications over the shared AI-gateway
 * socket. Backend pushes `notification.created` to the user's tenant
 * room on insert, so we don't need a dedicated SSE connection.
 *
 * Mounted once at the layout level — connect() is idempotent and
 * deduped, so other consumers (voice, AI assistant, scribe) reuse the
 * same socket without re-handshaking.
 */
export const useNotificationStream = (options: UseNotificationStreamOptions = {}) => {
    const {
        enabled = true,
        onNotification,
        showToast = true,
        autoInvalidate = true,
    } = options;

    const queryClient = useQueryClient();

    const handleNotification = useCallback(
        (notification: Notification) => {
            if (showToast) {
                const message = notification.message.en || notification.message.ar;

                switch (notification.priority) {
                    case 'urgent':
                        toast.error(notification.title.en || notification.title.ar, {
                            description: message,
                            duration: 10000,
                        });
                        break;
                    case 'high':
                        toast.warning(notification.title.en || notification.title.ar, {
                            description: message,
                            duration: 7000,
                        });
                        break;
                    default:
                        toast.info(notification.title.en || notification.title.ar, {
                            description: message,
                            duration: 5000,
                        });
                }
            }

            if (autoInvalidate) {
                queryClient.invalidateQueries({ queryKey: notificationKeys.lists() });
                queryClient.invalidateQueries({ queryKey: notificationKeys.unreadCount() });
                queryClient.invalidateQueries({ queryKey: notificationKeys.statistics() });
            }

            if (onNotification) {
                onNotification(notification);
            }
        },
        [showToast, autoInvalidate, onNotification, queryClient]
    );

    useEffect(() => {
        if (!enabled) return;

        // Connect on mount. Idempotent — if another feature already opened
        // the gateway socket, this resolves immediately.
        aiGatewayService.connect().catch((err) => {
            console.error('[useNotificationStream] AI gateway connect failed:', err);
        });

        const unsubscribe = aiGatewayService.on(
            'notification.created',
            (payload: unknown) => {
                handleNotification(payload as Notification);
            }
        );

        return unsubscribe;
    }, [enabled, handleNotification]);

    return {
        isConnected: aiGatewayService.isConnected(),
    };
};

// ==================== Utility Hooks ====================

/**
 * Hook that combines unread notifications and provides batch actions
 */
export const useUnreadNotifications = () => {
    const { data, ...queryResult } = useNotifications({ status: "sent" });
    const markAllAsRead = useMarkAllAsRead();
    const bulkDelete = useBulkDeleteNotifications();

    const notifications = data?.data || [];
    const count = data?.meta?.total || 0;

    return {
        notifications,
        count,
        markAllAsRead: markAllAsRead.mutate,
        deleteAll: () => {
            const ids = notifications.map((n) => n.id);
            if (ids.length > 0) {
                bulkDelete.mutate(ids);
            }
        },
        ...queryResult,
    };
};
